import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import useSolars from '../hooks/useSolars';

import { SolarType, View } from '../CommonTypes';

import { SolarTimeLine } from './SolarTimeLine';
import { SolarCompass } from './SolarCompass';
import useViews from '../hooks/useViews';

import ZoomableImage from './ZoomableImage';
import { SolarSceneSelect } from './SolarSceneSelect';

export const SolarViewer = ({ maxWidth }: { maxWidth?: string }) => {
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const [selectedSolar, setSelectedSolar] = useState<SolarType | null>(null);

  const [views] = useViews();
  const currentView = selectedView ? selectedView : views[0];

  const [solars, defaultIndex] = useSolars(currentView?.viewid);
  const currentSolar = selectedSolar ? selectedSolar : solars[defaultIndex || 0];

  // Keep track of which view's images we've already preloaded
  const preloadedByViewRef = useRef<Record<string, boolean>>({});

  // All image URLs for this view
  const allUrls = useMemo(() => solars.map(s => s.imageurl).filter(Boolean), [solars]);

  // Everything except the first image we show
  const restUrls = useMemo(() => {
    const first = currentSolar?.imageurl;
    return allUrls.filter(u => u !== first);
  }, [allUrls, currentSolar?.imageurl]);

  // Reset the one-time flag when the view changes
  useEffect(() => {
    // (No-op if viewid is falsy.)
  }, [currentView?.viewid]);

  const preloadImages = useCallback((urls: string[]) => {
    urls.forEach(url => {
      const img = new Image();
      // Hint to decode off the main thread when possible
      img.decoding = 'async';
      img.src = url;
    });
  }, []);

  const handleFirstImageLoad = useCallback(() => {
    const vid = currentView?.viewid;
    if (!vid) return;

    if (!preloadedByViewRef.current[vid]) {
      preloadedByViewRef.current[vid] = true;
      preloadImages(restUrls);
    }
  }, [currentView?.viewid, restUrls, preloadImages]);

  if (!currentSolar) return null;

  return (
    <div
      id="pintar-planner-map-wrapper"
      className={`m-auto`}
      style={{ maxWidth: maxWidth ? `clamp(0rem, ${maxWidth}, 90rem)` : '64rem' }}
    >
      <div className={`pintar-planner-content-wrapper relative`}>
        <SolarCompass compass={currentView?.compass || 'NE'} />

        {/* IMPORTANT: ZoomableImage must forward onLoad to the underlying <img>. */}
        <ZoomableImage src={currentSolar.imageurl} alt="MapImage" onLoad={handleFirstImageLoad} />

        <SolarSceneSelect currentView={currentView} setSelectedView={setSelectedView} />

        {/* SLIDER */}
        <div className="md:z-2 static flex max-h-screen w-full max-w-7xl flex-col items-center bg-black/90 p-4 md:absolute md:bottom-2 md:left-1/2 md:-translate-x-1/2 md:bg-black/30">
          <SolarTimeLine
            times={solars.map(solar => solar.time)}
            solars={solars}
            currentSolar={currentSolar}
            setSelectedSolar={setSelectedSolar}
          />
        </div>
      </div>
    </div>
  );
};
